/**
 * Examples.js — "Demo Data" page.
 *
 * One-click population of the live database with ready-made, persisted example
 * requirement stacks for demonstrations. Each import runs server-side (create
 * containers + requirements, then embed) and the result links straight into the
 * Vector Space explorer.
 *
 * #656 (owner decision, 2026-08-03): the packaged "dogfood" dataset — generated
 * at import time from a spec markdown doc (`examples_service.py`'s `kind ===
 * 'dogfood'` path) — errored on the dev deployment (`ProjectRequirements_V7.md`
 * not resolvable at the path the backend expected once packaged/deployed,
 * separate from whether the file exists in a local checkout). Rather than chase
 * path resolution across environments, the owner's call was to drop the option
 * from this page entirely — any `kind: 'dogfood'` entry the backend still lists
 * is filtered out here, so a future manifest entry of that kind doesn't silently
 * resurface a page nobody asked to keep maintaining.
 */
import * as React from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ScatterPlotRoundedIcon from '@mui/icons-material/ScatterPlotRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { examplesApi } from '../services/api';

// Unwrap an axios error into a readable message (backend sends `{detail}` on 4xx).
function errText(e) {
  const d = e && e.response && e.response.data;
  if (d && d.detail) return String(d.detail);
  return String((e && e.message) || e);
}

export default function Examples() {
  const navigate = useNavigate();
  const [examples, setExamples] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null); // id currently importing
  const [results, setResults] = React.useState({}); // id -> result/error

  const load = React.useCallback(() => {
    setError(null);
    // #361: go through the authed axios client (attaches bearer/org/project);
    // a bare fetch() omits the token and the backend rejects the list too.
    examplesApi.list()
      .then((data) => setExamples(data))
      .catch((e) => setError(errText(e)));
  }, []);
  React.useEffect(load, [load]);

  const runImport = async (id) => {
    setBusy(id);
    setResults((r) => ({ ...r, [id]: undefined }));
    try {
      // #361: authed import (bearer + X-Org-Id + X-Project-Id). Without an active
      // project the backend returns a clear error which we surface below.
      const body = await examplesApi.import(id);
      setResults((r) => ({ ...r, [id]: { ok: true, ...body } }));
    } catch (e) {
      setResults((r) => ({ ...r, [id]: { ok: false, error: errText(e) } }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>Demo Data &amp; Scripts</Typography>
      <Typography variant="body2" sx={{ mb: 1, maxWidth: 760, color: 'text.secondary' }}>
        Populate the live database with a ready-made requirement set, then explore it
        in the 3-D Vector Space — pick a set below, click <b>Import</b>, and play.
      </Typography>
      <Alert severity="info" sx={{ mb: 3, maxWidth: 760 }}>
        Importing <b>adds</b> to your current project (it does not clear it). Running an
        import twice creates a second copy. Embedding uses the configured model and may
        take a few seconds for large sets.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load examples: {error}</Alert>}
      {examples === null && !error && (
        <Stack direction="row" spacing={1} sx={{ color: 'text.secondary', alignItems: 'center' }}>
          <CircularProgress size={18} /> <span>Loading datasets…</span>
        </Stack>
      )}

      <Stack spacing={2} sx={{ maxWidth: 760 }}>
        {/* #656: the packaged dogfood dataset is removed from this page (owner
            decision) — filtered defensively by `kind` so it never resurfaces
            here even if the backend's manifest still lists one. */}
        {(examples || []).filter((ex) => ex.kind !== 'dogfood').map((ex) => {
          const r = results[ex.id];
          const importing = busy === ex.id;
          return (
            <Card key={ex.id} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 0.5, alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>{ex.name}</Typography>
                  <Chip size="small" label={ex.kind === 'dogfood' ? 'dogfood' : 'example'}
                    color={ex.kind === 'dogfood' ? 'secondary' : 'default'} />
                  {ex.count != null && (
                    <Chip size="small" variant="outlined"
                      label={`${ex.count} reqs · ${ex.containers} containers`} />
                  )}
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{ex.description}</Typography>
                {ex.error && <Alert severity="warning" sx={{ mt: 1 }}>Dataset error: {ex.error}</Alert>}

                {r && r.ok && (
                  <Alert severity="success" sx={{ mt: 1.5 }}>
                    Imported {r.requirements} requirements in {r.containers} containers
                    {r.embedded && r.embedded.added != null
                      ? ` · embedded ${r.embedded.added} new (total ${r.embedded.total})` : ''}.
                  </Alert>
                )}
                {r && !r.ok && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>Import failed: {r.error}</Alert>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
                  disabled={importing || ex.count == null}
                  onClick={() => runImport(ex.id)}
                >
                  {importing ? 'Importing…' : 'Import into live DB'}
                </Button>
                {r && r.ok && (
                  <Button startIcon={<ScatterPlotRoundedIcon />} onClick={() => navigate('/vector-space')}>
                    Open in Vector Space
                  </Button>
                )}
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
